import AnimateOnEnter from "@/ui/animations/AnimateOnEnter"
import Typography from "@/ui/atoms/Typography"
import ExpandedItemsList from "@/ui/molecules/ExpandedItemsList"
import InfoBar, { InfoBarItem } from "@/ui/molecules/InfoBar"
import SelectableItemsList, {
  SelectableItemsListItem,
} from "@/ui/molecules/SelectableItemsList"
import BlockContainer from "@/ui/structure/BlockContainer"
import Container from "@/ui/structure/Container"
import { classNames } from "@punks/ui-core"
import { ReactNode } from "react"

export interface FeaturesContainerProps {
  title?: ReactNode
  color?: "primary" | "light"
  decoration1?: ReactNode
  decoration2?: ReactNode
  card?: {
    title?: ReactNode
    imageDefault: ReactNode
    imageSelected?: ReactNode
    items: SelectableItemsListItem[]
    infoBarItems?: InfoBarItem[]
  }
}

const FeaturesContainer = ({
  title,
  color,
  decoration1,
  decoration2,
  card,
}: FeaturesContainerProps) => {
  return (
    <div
      className={classNames("relative overflow-hidden py-16 xl:py-32", {
        "bg-primary text-white": color === "primary",
      })}
    >
      <div className="hidden xl:block">
        {decoration1 && (
          <AnimateOnEnter delay={250} animationType="fadeIn">
            <div className="absolute inset-0 pointer-events-none">
              {decoration1}
            </div>
          </AnimateOnEnter>
        )}
      </div>

      {decoration2 && (
        <AnimateOnEnter delay={250} animationType="fadeIn">
          <div className="absolute inset-0 pointer-events-none">
            {decoration2}
          </div>
        </AnimateOnEnter>
      )}

      <Container>
        <Typography
          variant={["subtitle2", "h2"]}
          textAlign={"center"}
          weight={"bold"}
          className="max-w-[680px] mx-auto mt-16 mb-8 xl:mt-8 xl:mb-0"
        >
          {title}
        </Typography>
      </Container>

      {card && (
        <BlockContainer
          className="pt-8 sm:pt-32"
          type="card"
          classes={{
            card: "p-16 -mx-10 bg-background text-text z-[2]",
          }}
        >
          {card.title && (
            <Typography
              variant={"subtitle2"}
              weight={"bold"}
              className="mb-4"
              textAlign={"center"}
            >
              {card.title}
            </Typography>
          )}

          {card.items && card.items.length > 0 && (
            <div className="my-16">
              <SelectableItemsList
                className="hidden xl:block"
                items={card.items}
                imageDefault={card.imageDefault}
                imageSelected={card.imageSelected}
                classes={{
                  description: "sm:max-w-[680px] min-h-[120px]",
                }}
              />
              <ExpandedItemsList
                className="block xl:hidden"
                items={card.items}
                imageDefault={card.imageDefault}
                imageSelected={card.imageSelected}
                classes={{
                  description: "sm:max-w-[680px] min-h-[120px]",
                }}
              />
            </div>
          )}

          {card.infoBarItems && card.infoBarItems.length > 0 && (
            <InfoBar
              items={card.infoBarItems}
              className="mt-16 sm:mt-32 sm:mb-24"
            />
          )}
        </BlockContainer>
      )}
    </div>
  )
}

export default FeaturesContainer
